import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { SlidersHorizontal } from 'lucide-react';
import { toast } from 'react-toastify';
import { resolveBuyerSubSystem } from '../../constants/buyerSubSystems';
import { useVehicle } from '../../contexts/VehicleContext';
import { useLocationPreference } from '../../contexts/LocationPreferenceContext';
import { productService } from '../../services/apiService';
import {
  applyClientBrowseFilters,
  buildServerBrowseQuery,
  countActiveBrowseFilters,
  DEFAULT_BROWSE_FILTERS,
  parseBrowseFiltersFromSearchParams,
  writeBrowseFiltersToSearchParams,
  computePriceRangeHints,
} from '../../utils/catalogBrowseFilters';
import CatalogueDarkPartCard from '../CatalogueDarkPartCard';
import { ProductCardSkeleton } from '../Skeleton';
import CatalogBrowseFilterPanel, {
  CatalogBrowseFilterShell,
} from './CatalogBrowseFilterPanel';

const DEBOUNCE_MS = 300;

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function stripLeadMakeFromModel(model, make) {
  const m = String(model || '').trim();
  const mk = String(make || '').trim();
  if (!m || !mk) return m;
  if (m.toLowerCase().startsWith(`${mk.toLowerCase()} `)) {
    return m.slice(mk.length).trim() || m;
  }
  return m;
}

export default function SubSystemListing() {
  const { subSystemSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedVehicle } = useVehicle();
  const { searchLocation, chooseCurrentLocation, chooseRegisteredLocation, registeredLocation } =
    useLocationPreference();

  const subSystemEntry = useMemo(
    () => resolveBuyerSubSystem(subSystemSlug),
    [subSystemSlug]
  );

  const [filters, setFilters] = useState(() =>
    parseBrowseFiltersFromSearchParams(searchParams)
  );
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rawProducts, setRawProducts] = useState([]);

  const vehicleParams = useMemo(() => {
    const make = searchParams.get('vehicleMake') || selectedVehicle?.make || '';
    const modelRaw = searchParams.get('vehicleModel') || selectedVehicle?.model || '';
    return {
      vehicleMake: make,
      vehicleModel: stripLeadMakeFromModel(modelRaw, make),
      vehicleYear: searchParams.get('vehicleYear') || selectedVehicle?.year || '',
      vehicleVariant: searchParams.get('vehicleVariant') || selectedVehicle?.variant || '',
      vehicleFuelType: searchParams.get('vehicleFuelType') || selectedVehicle?.fuelType || '',
    };
  }, [searchParams, selectedVehicle]);

  const buyerCoords = useMemo(() => {
    if (searchLocation?.lat != null && searchLocation?.lng != null) {
      return { lat: searchLocation.lat, lng: searchLocation.lng };
    }
    return null;
  }, [searchLocation]);

  const proximityNeedsLocation =
    filters.proximity !== 'pan_india' && !buyerCoords?.lat;

  const serverFilters = useMemo(
    () => ({
      conditions: filters.conditions,
      sellerTypes: filters.sellerTypes,
      proximity: filters.proximity,
      rating: filters.rating,
    }),
    [filters.conditions, filters.sellerTypes, filters.proximity, filters.rating]
  );

  const debouncedServerFilters = useDebouncedValue(serverFilters, DEBOUNCE_MS);

  const activeFilterCount = countActiveBrowseFilters(filters);

  const syncUrl = useCallback(
    (nextFilters) => {
      const params = writeBrowseFiltersToSearchParams(nextFilters, searchParams);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleFiltersChange = useCallback(
    (next) => {
      setFilters(next);
      syncUrl(next);
    },
    [syncUrl]
  );

  const handleClearAll = useCallback(() => {
    const cleared = { ...DEFAULT_BROWSE_FILTERS };
    setFilters(cleared);
    syncUrl(cleared);
  }, [syncUrl]);

  useEffect(() => {
    setFilters(parseBrowseFiltersFromSearchParams(searchParams));
  }, [searchParams]);

  const fetchProducts = useCallback(async () => {
    if (!subSystemEntry) return;
    setLoading(true);
    try {
      const query = buildServerBrowseQuery({
        subSystemSlug: subSystemEntry.slug,
        filters: debouncedServerFilters,
        coords: buyerCoords,
        vehicleParams,
      });
      const res = await productService.getUserProducts(query);
      const list = res?.products ?? res?.items ?? [];
      setRawProducts(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error(err.message || 'Could not load parts');
      setRawProducts([]);
    } finally {
      setLoading(false);
    }
  }, [subSystemEntry, debouncedServerFilters, buyerCoords, vehicleParams]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const priceHints = useMemo(() => computePriceRangeHints(rawProducts), [rawProducts]);

  const displayProducts = useMemo(
    () => applyClientBrowseFilters(rawProducts, filters, buyerCoords),
    [rawProducts, filters, buyerCoords]
  );

  const handleRequestLocation = useCallback(async () => {
    try {
      if (registeredLocation) await chooseRegisteredLocation();
      else await chooseCurrentLocation();
    } catch {
      toast.info('Enable GPS or add a PIN in your account for proximity filters.');
    }
  }, [chooseCurrentLocation, chooseRegisteredLocation, registeredLocation]);

  if (!subSystemEntry) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-12 text-center">
        <p className="text-slate-600 mb-4">Unknown category.</p>
        <Link to="/discovery/subsystems" className="text-indigo-600 font-semibold">
          Back to browse
        </Link>
      </div>
    );
  }

  const Icon = subSystemEntry.icon;
  const vehicleQs = searchParams.toString();
  const categoriesLink = `/discovery/subsystem/${encodeURIComponent(
    subSystemEntry.backendKeys[0]
  )}${vehicleQs ? `?${vehicleQs}` : ''}`;

  const filterPanel = (
    <CatalogBrowseFilterPanel
      filters={filters}
      onChange={handleFiltersChange}
      onClearAll={handleClearAll}
      priceHints={priceHints}
      proximityNeedsLocation={proximityNeedsLocation}
      onRequestLocation={handleRequestLocation}
    />
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <button
          type="button"
          onClick={() => navigate('/discovery/subsystems')}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4"
        >
          <FaArrowLeft /> Browse categories
        </button>

        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700">
              <Icon className="h-6 w-6" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{subSystemEntry.label}</h1>
              <p className="text-sm text-slate-600">
                {loading ? 'Loading…' : `${displayProducts.length} listing${displayProducts.length === 1 ? '' : 's'}`}
                {selectedVehicle?.make
                  ? ` · ${selectedVehicle.make} ${selectedVehicle.model || ''}`
                  : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={categoriesLink}
              className="text-sm font-semibold text-indigo-600 hover:underline"
            >
              Browse part categories →
            </Link>
          </div>
        </header>

        <div className="flex gap-6">
          <CatalogBrowseFilterShell
            open={mobileFiltersOpen}
            onClose={() => setMobileFiltersOpen(false)}
            activeCount={activeFilterCount}
          >
            {filterPanel}
          </CatalogBrowseFilterShell>

          <main className="flex-1 min-w-0">
            <div className="lg:hidden mb-4">
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold shadow-sm"
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              </button>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {[...Array(8)].map((_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
            ) : displayProducts.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-white p-12 text-center">
                <p className="text-slate-600 mb-4">No parts match your filters.</p>
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="text-sm font-semibold text-indigo-600 hover:underline"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {displayProducts.map((product, index) => (
                  <CatalogueDarkPartCard
                    key={product._id || product.id || index}
                    product={product}
                    index={index}
                    linkTarget="product"
                  />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
